import React, { useState } from 'react';
import { Building2, X, Sparkles, AlertCircle } from 'lucide-react';
import { useTenant } from '../../context/TenantContext.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { createTenant } = useTenant();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    if (!slug || slug === name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
      setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Organization name is required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await createTenant({
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
      });
      setName('');
      setSlug('');
      setDescription('');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in font-sans">
      <div className="w-full max-w-md bg-white dark:bg-[#121417] text-[#121417] dark:text-white rounded-3xl border border-border shadow-neo-float p-6 relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-500/10 text-brand-500 dark:text-brand-400 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-extrabold tracking-tight">Create Organization</h3>
              <p className="text-xs text-text-muted">Set up a new isolated multi-tenant workspace</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-text-muted hover:text-text hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 flex items-center space-x-2 text-rose-600 dark:text-rose-400 text-xs">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-text mb-1">Organization Name *</label>
            <input
              type="text"
              required
              value={name}
              onChange={handleNameChange}
              placeholder="e.g. Acme Innovations Corp"
              className="w-full px-3.5 py-2.5 rounded-2xl bg-surface-50 dark:bg-surface-900 border border-border text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-text mb-1">URL Identifier (Slug)</label>
            <div className="flex items-center rounded-2xl bg-surface-50 dark:bg-surface-900 border border-border px-3.5 py-2.5 text-xs">
              <span className="text-text-muted select-none">pulse.ihb/</span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="acme-innovations"
                className="w-full bg-transparent text-text focus:outline-none ml-1 font-mono text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-text mb-1">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this organization work on?"
              className="w-full px-3.5 py-2.5 rounded-2xl bg-surface-50 dark:bg-surface-900 border border-border text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all resize-none"
            />
          </div>

          <div className="p-3 rounded-2xl bg-brand-500/5 border border-brand-500/20 text-[11px] text-text-muted flex items-start space-x-2">
            <Sparkles className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" />
            <span>You will automatically be assigned as <strong>Owner</strong>. You can create custom teams, invite members, and build boards.</span>
          </div>

          <div className="flex items-center justify-end space-x-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-2xl text-xs font-bold text-text-muted hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-5 py-2.5 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-pulse transition-all disabled:opacity-50 flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <span>Create Organization</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
